/**
 * Invoice-related interfaces following CourierBoost patterns
 */

import type { InterfaceGenericTypeSlug } from './InterfaceGenericTypeSlug';

/**
 * Invoice status enum
 */
export enum InvoiceStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  SENT = 'sent',
  PAID = 'paid',
  PARTIALLY_PAID = 'partially_paid',
  OVERDUE = 'overdue',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
}

/**
 * Payment method types
 */
export enum PaymentMethod {
  CASH = 'cash',
  CREDIT_CARD = 'credit_card',
  DEBIT_CARD = 'debit_card',
  BANK_TRANSFER = 'bank_transfer',
  CHECK = 'check',
  ONLINE = 'online',
  OTHER = 'other',
}

/**
 * Main invoice interface
 */
export interface InterfaceInvoice {
  uuid: string;
  invoiceNumber: string;
  companyUuid: string;
  customerUuid: string;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  billingAddressUuid?: string;

  // Dates
  invoiceDate: string;
  dueDate: string;
  paidDate?: string;

  // Financial
  subtotal: number; // in cents
  taxRate: number; // percentage
  taxAmount: number; // in cents
  discount: number; // in cents
  total: number; // in cents
  amountPaid: number; // in cents
  amountDue: number; // in cents

  // Status
  status: InvoiceStatus;

  // Content
  lineItems: InterfaceInvoiceLineItem[];
  notes?: string;
  terms?: string;

  // Metadata
  createdAt: string;
  updatedAt: string;
  createdByUuid: string;
  updatedByUuid?: string;

  // Relations
  payments?: InterfaceInvoicePayment[];
  deliveryUuids?: string[];
}

/**
 * Invoice line item interface
 */
export interface InterfaceInvoiceLineItem {
  uuid: string;
  invoiceUuid: string;
  description: string;
  quantity: number;
  unitPrice: number; // in cents
  total: number; // in cents
  sortOrder: number;

  // Optional fields for linking to deliveries
  deliveryUuid?: string;
  deliveryReference?: string;
}

/**
 * Invoice payment record interface
 */
export interface InterfaceInvoicePayment {
  uuid: string;
  invoiceUuid: string;
  amount: number; // in cents
  paymentMethod: PaymentMethod;
  paymentDate: string;
  reference?: string;
  notes?: string;

  // Payment processor info
  transactionId?: string;
  processorResponse?: string;

  // Metadata
  createdAt: string;
  createdByUuid: string;
}

/**
 * Invoice type definition
 */
export interface InterfaceInvoiceType extends InterfaceGenericTypeSlug {
  slug: string;
  name: string;
}

/**
 * Form interfaces for CRUD operations
 */

export interface InterfaceCreateInvoiceForm {
  customerUuid: string;
  billingAddressUuid?: string;
  invoiceDate: string;
  dueDate: string;
  paymentTerms?: string;
  status?: InvoiceStatus;
  lineItems: InterfaceCreateInvoiceLineItemForm[];
  subtotal?: number;
  tax?: number;
  taxRate?: number;
  discount?: number;
  total?: number;
  notes?: string;
  termsAndConditions?: string;
  terms?: string;
  deliveryUuids?: string[];
}

export interface InterfaceCreateInvoiceLineItemForm {
  description: string;
  quantity: number;
  unitPrice: number; // in cents
  total?: number; // in cents
  sortOrder?: number;
  deliveryUuid?: string;
}

export interface InterfaceUpdateInvoiceForm {
  customerUuid?: string;
  billingAddressUuid?: string;
  invoiceDate?: string;
  dueDate?: string;
  taxRate?: number;
  discount?: number;
  notes?: string;
  terms?: string;
  status?: InvoiceStatus;
}

export interface InterfaceUpdateInvoiceLineItemForm {
  description?: string;
  quantity?: number;
  unitPrice?: number;
}

export interface InterfaceRecordPaymentForm {
  amount: number; // in cents
  paymentMethod: PaymentMethod;
  paymentDate?: string;
  reference?: string;
  notes?: string;
  transactionId?: string;
}

/**
 * URL variables for thunk helpers
 */
export interface InterfaceInvoiceUrlVars {
  invoiceUuid: string;
}

export interface InterfaceInvoiceLineItemUrlVars {
  invoiceUuid: string;
  lineItemUuid: string;
}

export interface InterfaceInvoicePaymentUrlVars {
  invoiceUuid: string;
  paymentUuid: string;
}

/**
 * Helper type for invoice calculations
 */
export interface InterfaceInvoiceTotals {
  subtotal: number;
  taxAmount: number;
  discount: number;
  total: number;
  amountPaid: number;
  amountDue: number;
}

/**
 * Filter interface for invoice list
 */
export interface InterfaceInvoiceFilters {
  status?: InvoiceStatus;
  customerUuid?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  perPage?: number;
  sortBy?: 'invoiceNumber' | 'invoiceDate' | 'dueDate' | 'total' | 'status';
  sortOrder?: 'asc' | 'desc';
}
